import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, TrendingUp } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { LANGUAGES, CURRENCIES } from "@/lib/i18n";

export default function SettingsPanel() {
  const [s, setS] = useState(null);
  const [levels, setLevels] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/settings");
    setS(data.settings); setLevels(data.levels);
  };
  useEffect(() => { load(); }, []);
  if (!s) return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        available_capital: parseFloat(s.available_capital) || 0,
        level: s.level,
        min_margin: parseFloat(s.min_margin) || 0,
        max_risk: s.max_risk,
        base_currency: s.base_currency,
        language: s.language,
        allowed_countries: typeof s.allowed_countries === "string" ? s.allowed_countries.split(",").map((x) => x.trim()).filter(Boolean) : s.allowed_countries,
        categories: typeof s.categories === "string" ? s.categories.split(",").map((x) => x.trim()).filter(Boolean) : s.categories,
        auto_reinvest: s.auto_reinvest,
        auto_reinvest_authorized: s.auto_reinvest_authorized,
      };
      const { data } = await api.put("/settings", payload);
      setS(data.settings);
      toast.success("Configurações salvas");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 animate-float-up max-w-4xl">
      <div className="rounded-xl border border-border bg-card p-6 space-y-4">
        <h3 className="font-heading font-semibold">Limite de investimento por produto</h3>
        <p className="text-sm text-muted-foreground">A IA nunca ultrapassa este limite sem sua autorização. Comece no Nível 1 e escale quando decidir.</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {levels.map((l) => (
            <button key={l.level} onClick={() => set("level", l.level)} data-testid={`level-${l.level}`}
              className={`rounded-xl border p-4 text-left transition-colors ${s.level === l.level ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}>
              <p className="text-xs font-mono text-muted-foreground">Nível {l.level}</p>
              <p className="font-heading font-bold">R${l.brl}</p>
              <p className="text-xs text-muted-foreground">US${l.usd}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-6 grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5"><Label className="text-xs">Capital disponível</Label><Input type="number" step="0.01" value={s.available_capital} onChange={(e) => set("available_capital", e.target.value)} data-testid="set-capital" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Margem mínima (%)</Label><Input type="number" step="0.1" value={s.min_margin} onChange={(e) => set("min_margin", e.target.value)} data-testid="set-margin" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Risco máximo</Label>
          <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={s.max_risk} onChange={(e) => set("max_risk", e.target.value)} data-testid="set-risk">
            {["BAIXO", "MÉDIO", "ALTO"].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="space-y-1.5"><Label className="text-xs">Moeda base</Label>
          <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={s.base_currency} onChange={(e) => set("base_currency", e.target.value)} data-testid="set-currency">
            {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
          </select>
        </div>
        <div className="space-y-1.5"><Label className="text-xs">Idioma do atendimento</Label>
          <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={s.language} onChange={(e) => set("language", e.target.value)} data-testid="set-language">
            {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </div>
        <div className="space-y-1.5"><Label className="text-xs">Países permitidos (separados por vírgula)</Label><Input value={Array.isArray(s.allowed_countries) ? s.allowed_countries.join(", ") : s.allowed_countries} onChange={(e) => set("allowed_countries", e.target.value)} data-testid="set-countries" /></div>
        <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Categorias (separadas por vírgula)</Label><Input value={Array.isArray(s.categories) ? s.categories.join(", ") : s.categories} onChange={(e) => set("categories", e.target.value)} data-testid="set-categories" /></div>
      </div>

      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-6 space-y-4">
        <div className="flex items-center gap-2"><TrendingUp className="h-5 w-5 text-amber-500" /><h3 className="font-heading font-semibold">Reinvestimento automático</h3></div>
        <div className="flex items-center justify-between">
          <div><p className="text-sm">Ativar reinvestimento automático</p><p className="text-xs text-muted-foreground">Requer autorização explícita antes de qualquer compra ou operação financeira.</p></div>
          <Switch checked={s.auto_reinvest} onCheckedChange={(v) => set("auto_reinvest", v)} data-testid="set-reinvest" />
        </div>
        {s.auto_reinvest && (
          <div className="flex items-center justify-between border-t border-border pt-4">
            <p className="text-sm">Autorizo a IA a executar reinvestimentos dentro dos meus limites.</p>
            <Switch checked={s.auto_reinvest_authorized} onCheckedChange={(v) => set("auto_reinvest_authorized", v)} data-testid="set-reinvest-auth" />
          </div>
        )}
      </div>

      <Button onClick={save} disabled={saving} data-testid="settings-save"><Save className="h-4 w-4 mr-1" /> {saving ? "Salvando…" : "Salvar configurações"}</Button>
    </div>
  );
}
